import { Type, type Static } from "@sinclair/typebox";
import {
  AgentInvocationAuthorizationRequestBase,
  MatrixEventIdSchema,
  WORKBENCH_AGENT_SCHEMA_VERSION
} from "./agent-host.ts";
import { AuthorizationContextSchema } from "./document-platform.ts";
import { KnowledgeBaseDirectoryEntryV2Schema } from "./knowledge-query-compilation.ts";
import { RetrievalCitationSchema } from "./knowledge-retrieval.ts";

export const KNOWLEDGE_QUERY_TOOL_KEY = "query_knowledge" as const;

export const KnowledgeQueryToolAuthorizationRequestSchema = Type.Union([
  Type.Object(
    {
      ...AgentInvocationAuthorizationRequestBase,
      roomKind: Type.Literal("groupRoom"),
      threadRootEventId: MatrixEventIdSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      ...AgentInvocationAuthorizationRequestBase,
      roomKind: Type.Literal("directChat"),
      threadRootEventId: Type.Union([MatrixEventIdSchema, Type.Null()])
    },
    { additionalProperties: false }
  )
]);

export const AuthorizedKnowledgeBaseSchema = Type.Object(
  {
    directoryEntry: KnowledgeBaseDirectoryEntryV2Schema,
    authorizationContext: AuthorizationContextSchema
  },
  { additionalProperties: false }
);

export const KnowledgeQueryToolAuthorizationResultSchema = Type.Object(
  {
    schemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
    authorizedKnowledgeBases: Type.Array(AuthorizedKnowledgeBaseSchema, {
      minItems: 1,
      maxItems: 64
    })
  },
  { additionalProperties: false }
);

export const KnowledgeQueryToolInputSchema = Type.Object(
  {
    activityText: Type.String({ minLength: 1, maxLength: 256 })
  },
  { additionalProperties: false }
);

export const KnowledgeQueryToolEvidenceSchema = Type.Object(
  {
    evidenceId: Type.String({ pattern: "^E[1-9][0-9]*$" }),
    content: Type.String({ minLength: 1, maxLength: 8_000 }),
    citation: RetrievalCitationSchema
  },
  { additionalProperties: false }
);

export const KnowledgeQueryToolResultSchema = Type.Object(
  { evidence: Type.Array(KnowledgeQueryToolEvidenceSchema, { maxItems: 64 }) },
  { additionalProperties: false }
);

export const KnowledgeQueryToolContractSchema = Type.Object(
  {
    schemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
    toolKey: Type.Literal(KNOWLEDGE_QUERY_TOOL_KEY),
    authorizationMode: Type.Literal("acting_human_obo"),
    inputSchemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION),
    resultSchemaVersion: Type.Literal(WORKBENCH_AGENT_SCHEMA_VERSION)
  },
  { additionalProperties: false }
);

export const KNOWLEDGE_QUERY_TOOL_CONTRACT = Object.freeze({
  schemaVersion: WORKBENCH_AGENT_SCHEMA_VERSION,
  toolKey: KNOWLEDGE_QUERY_TOOL_KEY,
  authorizationMode: "acting_human_obo" as const,
  inputSchemaVersion: WORKBENCH_AGENT_SCHEMA_VERSION,
  resultSchemaVersion: WORKBENCH_AGENT_SCHEMA_VERSION
});

export type KnowledgeQueryToolAuthorizationRequestV1 = Static<
  typeof KnowledgeQueryToolAuthorizationRequestSchema
>;
export type AuthorizedKnowledgeBaseV1 = Static<typeof AuthorizedKnowledgeBaseSchema>;
export type KnowledgeQueryToolAuthorizationResultV1 = Static<
  typeof KnowledgeQueryToolAuthorizationResultSchema
>;
export type KnowledgeQueryToolInputV1 = Static<typeof KnowledgeQueryToolInputSchema>;
export type KnowledgeQueryToolEvidenceV1 = Static<typeof KnowledgeQueryToolEvidenceSchema>;
export type KnowledgeQueryToolResultV1 = Static<typeof KnowledgeQueryToolResultSchema>;
export type KnowledgeQueryToolContractV1 = Static<typeof KnowledgeQueryToolContractSchema>;
